import { HStack, Skeleton } from '@chakra-ui/react';
import {
  ContactType,
  TransactionSide,
} from '@client/graphql/__generated__/types';
import { useParams, useSearchParams } from 'react-router-dom';
import { useNotificationsContext } from '../../context/NotificationsContext';
import { useNotificationTemplates } from '../../hooks/useNotificationTemplates';
import { PIPELINE_COLUMNS } from './constants';
import { PipelineColumn } from './PipelineColumn';

const isValidTransactionSide = (
  value: string | null
): value is TransactionSide =>
  value !== null &&
  Object.values(TransactionSide).includes(value as TransactionSide);

const isValidContactType = (value: string | null): value is ContactType =>
  value !== null && Object.values(ContactType).includes(value as ContactType);

interface PipelineKanbanProps {
  partnerId?: string;
  side?: TransactionSide;
}

export const PipelineKanban = ({ partnerId, side }: PipelineKanbanProps) => {
  const [searchParams] = useSearchParams();
  const { partnerId: routePartnerId } = useParams<{ partnerId: string }>();
  const { partners } = useNotificationsContext();

  // Determine if we're in partner detail view or aggregated view
  const isPartnerView = !!routePartnerId;
  const effectivePartnerId = partnerId ?? routePartnerId;

  // For aggregated view, get filters from URL
  const filterPartnerId = searchParams.get('partner') ?? '';
  const sideParam = searchParams.get('side');
  const filterSide = isValidTransactionSide(sideParam) ? sideParam : undefined;
  const recipientParam = searchParams.get('recipient');
  const filterRecipient = isValidContactType(recipientParam)
    ? recipientParam
    : undefined;

  // Determine which partner to fetch templates for
  const targetPartnerId =
    effectivePartnerId || filterPartnerId || partners[0]?.id;

  const { templatesByEventType, loading } = useNotificationTemplates({
    partnerId: targetPartnerId ?? '',
    side: side ?? filterSide,
    recipient: filterRecipient,
    skip: !targetPartnerId,
  });

  if (loading) {
    return (
      <HStack
        css={{
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '4px',
          },
        }}
        height="100%"
        overflowX="auto"
        pb={4}
        pl={{ base: 0, md: 6, lg: 12 }}
        spacing={3}
      >
        {PIPELINE_COLUMNS.map((col) => (
          <Skeleton
            key={col.eventType}
            borderRadius="2xl"
            flexShrink={0}
            height="100%"
            width="345px"
          />
        ))}
      </HStack>
    );
  }

  return (
    <HStack
      alignItems="stretch"
      css={{
        '&::-webkit-scrollbar': {
          height: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '4px',
        },
      }}
      height="100%"
      overflowX="auto"
      pb={4}
      pl={{ base: 0, md: 6, lg: 12 }}
      spacing={3}
    >
      {PIPELINE_COLUMNS.map((column) => (
        <PipelineColumn
          key={column.eventType}
          eventType={column.eventType}
          icon={column.icon}
          label={column.label}
          showAddButton={isPartnerView}
          showPartner={!isPartnerView}
          templates={templatesByEventType[column.eventType] ?? []}
        />
      ))}
    </HStack>
  );
};
